//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import angular, {
  IHttpService,
  IPromise,
  IRequestShortcutConfig,
} from 'angular';

import * as MetricsGatewayApiModel from '@models/MetricsGatewayApiModels';
import { saveBlob } from '@utils/saveBlob';
import { gatewayFetch } from '@utils/sessionFetch';

export default class MetricsGatewayApiService {
  private baseUrl: string;
  private emailSignupBaseUrl: string;

  constructor(private $http: IHttpService, private SessionVarsService) {
    const metricsGatewayUrl = this.SessionVarsService.getBaseUrl(
      'metrics_gateway',
    );
    this.baseUrl = `${metricsGatewayUrl}/api/v1/unified-metrics`;
    this.emailSignupBaseUrl = `${metricsGatewayUrl}/api/v1/emailSignup`;
  }

  public getMetadata(): IPromise<MetricsGatewayApiModel.IMetadataResponse> {
    return this.$http
      .get(`${this.baseUrl}/metadata`)
      .then(
        (response) => response.data as MetricsGatewayApiModel.IMetadataResponse,
      );
  }

  public getBubbleStats(
    statsRequest: MetricsGatewayApiModel.IStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IBubbleStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: statsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/bubble-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IBubbleStatsResponse[],
      );
  }

  public getTouchpointStats(
    statsRequest: MetricsGatewayApiModel.IStatsRequest,
  ): IPromise<MetricsGatewayApiModel.ITouchpointStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: statsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/touchpoint-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.ITouchpointStatsResponse[],
      );
  }

  public getGraphStats(
    statsRequest: MetricsGatewayApiModel.IStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IGraphStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: statsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/graph-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IGraphStatsResponse[],
      );
  }

  public getLiftStats(
    statsRequest: MetricsGatewayApiModel.IStatsRequest,
  ): IPromise<MetricsGatewayApiModel.ILiftStatsResponse> {
    const queryParams: IRequestShortcutConfig = {
      params: statsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/lift-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.ILiftStatsResponse,
      );
  }

  public getItemStats(
    itemProductStatsRequest: MetricsGatewayApiModel.IItemProductStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IItemStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: this.buildItemProductRequestQueryParams(itemProductStatsRequest),
    };
    return this.$http
      .get(`${this.baseUrl}/item-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IItemStatsResponse[],
      );
  }

  public getBenchmarkStats(
    benchmarkStatsRequest: MetricsGatewayApiModel.IBenchmarkStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IBenchmarkStatsResponse> {
    const queryParams: IRequestShortcutConfig = {
      params: benchmarkStatsRequest,
    };

    return this.$http
      .get(`${this.baseUrl}/benchmarks`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IBenchmarkStatsResponse,
      );
  }

  public getProductStats(
    itemProductStatsRequest: MetricsGatewayApiModel.IItemProductStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IProductStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: this.buildItemProductRequestQueryParams(itemProductStatsRequest),
    };
    return this.$http
      .get(`${this.baseUrl}/product-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IProductStatsResponse[],
      );
  }

  public getContributorStats(
    itemProductStatsRequest: MetricsGatewayApiModel.IItemProductStatsRequest,
  ): IPromise<MetricsGatewayApiModel.IContributorStatsResponse[]> {
    const queryParams: IRequestShortcutConfig = {
      params: itemProductStatsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/contributor-stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IContributorStatsResponse[],
      );
  }

  public generateEmailsCsvDownloadUrl(touchpoint) {
    return `${this.emailSignupBaseUrl}/${touchpoint.label}/emails.csv`;
  }

  public downloadEmailsCsv(url, params, filename) {
    return this.$http.get(url, { params }).then((response) => {
      saveBlob(response.data as string, filename, response.headers);
    });
  }

  public getEmailsCount(
    emailStatsRequest: MetricsGatewayApiModel.IEmailStatsRequest,
    touchpoint,
  ): IPromise<MetricsGatewayApiModel.IEmailsCountResponse> {
    const queryParams: IRequestShortcutConfig = {
      params: emailStatsRequest,
    };

    return this.$http
      .get(
        `${this.emailSignupBaseUrl}/${touchpoint.label}/email-count`,
        queryParams,
      )
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IEmailsCountResponse,
      );
  }

  public getEmailsEnabled(
    touchpoint,
  ): IPromise<MetricsGatewayApiModel.IEmailEnabledResponse> {
    return this.$http
      .get(`${this.emailSignupBaseUrl}/${touchpoint.label}/enabled`)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IEmailEnabledResponse,
      );
  }

  public getContentAcquisition(
    contentAcquisitionRequest,
  ): IPromise<MetricsGatewayApiModel.IContentAcquisitionResponse> {
    const queryParams: IRequestShortcutConfig = {
      params: contentAcquisitionRequest,
    };
    const webGatewayUrl = this.SessionVarsService.getBaseUrl('web_gateway');
    return this.$http
      .get(`${webGatewayUrl}/api/v1/media/stats`, queryParams)
      .then(
        (response) =>
          response.data as MetricsGatewayApiModel.IContentAcquisitionResponse,
      );
  }

  public getZipExportUrl() {
    return `${this.baseUrl}/export.zip`;
  }

  /**
   * Get a list of labels with their count
   *
   * /api/v1/unified-metrics/labels
   *
   * @param interval date range to query from
   */
  public getLabels(labelsRequest: MetricsGatewayApiModel.ILabelsRequest) {
    const queryParams: IRequestShortcutConfig = {
      params: labelsRequest,
    };
    return this.$http
      .get(`${this.baseUrl}/labels`, queryParams)
      .then(
        (response) => response.data as MetricsGatewayApiModel.ILabelsResponse[],
      );
  }

  private buildItemProductRequestQueryParams = (
    req: MetricsGatewayApiModel.IItemProductStatsRequest,
  ): MetricsGatewayApiModel.IItemProductStatsRequestWithContainer => {
    // Exclude item/product stats attributed to the `banner-image` containerId for the L2B source (and summary page)
    // TODO: Once these api calls are ported out of this angular service
    //  1. Restructure the `IItemProductStatsRequest` interface such that we don't need to
    //     conditionally check source type when building the params
    //  2. Define constants for the various source types
    //  3. Define a constant for the 'banner-image' containerId
    const isL2BSource = req.sourceType === 'L2B' || req.sourceType === '';
    return {
      ...req,
      containerId: isL2BSource ? 'banner-image' : undefined,
      excludeContainerId: isL2BSource ? true : undefined,
    };
  };
}

const UNIFIED_METRICS_BASE_URL = '/api/v1/unified-metrics';

/**
 * Get tagged product stats for a client for a given date range
 *
 * /api/v1/unified-metrics/tagged-product-stats
 *
 * @param itemProductStatsRequest body request to send to the endpoint
 */
export async function getTaggedProductStats(
  itemProductStatsRequest: MetricsGatewayApiModel.IItemProductStatsRequest,
) {
  const queryParams = $.param(itemProductStatsRequest);
  const res = await gatewayFetch(
    'metrics_gateway',
    `${UNIFIED_METRICS_BASE_URL}/tagged-product-stats?${queryParams}`,
  );
  return (await res.json()) as MetricsGatewayApiModel.IProductStatsResponse[];
}

/**
 * Get csv export download for a client's tagged products
 *
 * /api/v1/unified-metrics/tagged-product-stats.csv
 *
 * @param interval date range to query from
 */
export async function downloadPdpCsv(interval: string) {
  const params = {
    interval,
  };
  const queryParams = $.param(params);
  const res = await gatewayFetch(
    'metrics_gateway',
    `${UNIFIED_METRICS_BASE_URL}/tagged-product-stats.csv?${queryParams}`,
  );
  return await res.text();
}

/**
 * Get zip export download for a client's tagged products
 *
 * /api/v1/unified-metrics/tagged-product-stats.zip
 *
 * @param interval date range to query from
 */
export async function downloadPdpZip(interval: string) {
  const params = {
    interval,
  };
  const queryParams = $.param(params);
  const res = await gatewayFetch(
    'metrics_gateway',
    `${UNIFIED_METRICS_BASE_URL}/tagged-product-stats.zip?${queryParams}`,
  );
  return await res.blob();
}

angular
  .module('curalate')
  .service('MetricsGatewayApiService', [
    '$http',
    'SessionVarsService',
    MetricsGatewayApiService,
  ]);
